import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    effect,
    inject, OnDestroy, signal,
    untracked,
    viewChildren,
    ViewEncapsulation
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';

import { RegistrarService } from '@app/services/registrar.service';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { MenuStateService } from '@services/menu-state.service';
import { SidenavMobileComponent } from './../sidenav-mobile/sidenav-mobile.component';

@Component( {
    selector: 'app-sidenav',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        RouterLinkActive,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatBottomSheetModule,
        MatTooltipModule
    ],
    templateUrl: './sidenav.component.html',
    styleUrl: './sidenav.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: { '(document:click)': 'onDocumentClick($event)' }
} )
export class SidenavComponent implements AfterViewInit, OnDestroy {
    readonly auth = inject( AuthService );
    readonly #router = inject( Router );
    readonly #bottomSheet = inject( MatBottomSheet );
    readonly #menuState = inject( MenuStateService );
    readonly #registrarService = inject( RegistrarService );

    readonly menuTriggers = viewChildren( MatMenuTrigger );

    readonly expanded = signal<boolean>( false );
    readonly currentUser = this.auth.currentUser;
    protected readonly organizationLabel = this.#registrarService.organizationLabel;

    // ── Feature flags ─────────────────────────────────────────────────────────
    readonly showUnLocodes = environment.optionalModules.showUnLocodes;
    readonly showUserAdmin = environment.optionalModules.showUserAdmin;
    readonly showService = environment.optionalModules.showServiceModules;

    // Signal-based URL so isRouteActive re-evaluates on every navigation
    readonly #currentUrl = toSignal( this.#router.events.pipe( filter( ( e ) => e instanceof NavigationEnd ) ), {
        initialValue: null
    } );

    constructor() {
        // Close own menus when toolbar opens one — ignore own emissions to avoid self-close.
        effect( () => {
            const source = this.#menuState.menuOpened();
            if ( source && source !== 'sidenav' ) {
                this.closeAllMenus();
            }
        } );

        effect( () => {
            const scopeTenantID = this.currentUser()?.scopeTenantID;

            untracked( () => {
                if ( scopeTenantID ) {
                    this.#registrarService.getScopeTenantByID( scopeTenantID ).subscribe();
                }
            } );
        } );
    }

    ngAfterViewInit(): void {
        this.#updateFlyoutPosition();
    }

    // ── Menus ─────────────────────────────────────────────────────────────────

    closeAllMenus(): void {
        this.menuTriggers()?.forEach( ( t ) => {
            if ( t.menuOpen ) t.closeMenu();
        } );
    }

    closeOtherMenus( openTrigger: MatMenuTrigger ): void {
        this.menuTriggers().forEach( ( t ) => {
            if ( t !== openTrigger && t.menuOpen ) t.closeMenu();
        } );
    }

    /** Close all menus on any click outside the flyout panel or a menu trigger. */
    onDocumentClick( event: MouseEvent ): void {
        const target = event.target as HTMLElement;
        const inOverlay = target.closest( '.cdk-overlay-container' );
        const onTrigger = target.closest( '.sidenav__link--trigger' );
        if ( !inOverlay && !onTrigger ) {
            this.closeAllMenus();
        }
    }

    /** Called from (menuOpened) in template — closes siblings and notifies toolbar. */
    onMenuOpened( trigger: MatMenuTrigger ): void {
        this.closeOtherMenus( trigger );
        this.#menuState.notifyMenuOpened( 'sidenav' );
    }

    isRouteActive( routes: string[] ): boolean {
        this.#currentUrl(); // signal subscription so Angular re-evaluates on navigation
        const currentUrl = this.#router.url.split( '?' )[ 0 ].split( ';' )[ 0 ];
        return routes.some( ( r ) => currentUrl.startsWith( r ) );
    }

    // ── Desktop ───────────────────────────────────────────────────────────────

    toggleExpanded(): void {
        this.expanded.update( ( v ) => !v );
        this.closeAllMenus();
        this.#updateFlyoutPosition();
    }

    #updateFlyoutPosition(): void {
        const w = this.expanded() ? '220px' : '64px';
        document.documentElement.style.setProperty( '--sidenav-current-width', w );
    }

    // ── Mobile ────────────────────────────────────────────────────────────────

    openMobileMenu(): void {
        this.#bottomSheet.open( SidenavMobileComponent, {
            panelClass: 'sidenav__bottom-sheet'
        } );
    }

    ngOnDestroy(): void {
        this.#bottomSheet.dismiss();
    }
}
